import { Fragment } from 'react';

import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

const SCALE: readonly ColorToken[] = [
  'Brand/50',
  'Brand/100',
  'Brand/200',
  'Brand/400',
  'Brand/600',
];

const LIGHT_TEXT_FROM = 3;

export type HeatColumn = Readonly<{ id: string; label: string }>;

export type HeatRow = Readonly<{
  id: string;
  label: string;
  counts: Readonly<Record<string, number>>;
}>;

export type HeatGridProps = Readonly<{
  rows: readonly HeatRow[];
  columns: readonly HeatColumn[];
  xAxisLabel: string;
  yAxisLabel: string;
  /** Predicate for which cells to outline. */
  isHighlighted?: (rowId: string, columnId: string) => boolean;
}>;

/** Counts on a two-axis grid, shaded by how large each cell is. */
export const HeatGrid = ({
  rows,
  columns,
  xAxisLabel,
  yAxisLabel,
  isHighlighted,
}: HeatGridProps) => {
  const max = Math.max(
    ...rows.flatMap((row) => columns.map((column) => row.counts[column.id] ?? 0)),
    0,
  );

  const scaleIndex = (value: number) => {
    if (value <= 0 || max <= 0) return 0;

    return Math.min(SCALE.length - 1, Math.ceil((value / max) * SCALE.length) - 1);
  };

  return (
    <Box sx={outerSx}>
      <Typography variant="text-xs-medium" sx={yAxisSx}>
        {yAxisLabel}
      </Typography>
      <Box sx={rightSx}>
        <Box sx={gridSx(columns.length)}>
          {rows.map((row) => (
            <Fragment key={row.id}>
              <Typography variant="text-xs-medium" sx={rowLabelSx}>
                {row.label}
              </Typography>
              {columns.map((column) => {
                const value = row.counts[column.id] ?? 0;
                const index = scaleIndex(value);

                return (
                  <Box
                    key={column.id}
                    sx={cellSx(Boolean(isHighlighted?.(row.id, column.id)))}
                    style={{ backgroundColor: COLOR_TOKENS[SCALE[index]] }}
                  >
                    <Typography
                      variant="text-xs-medium"
                      sx={index >= LIGHT_TEXT_FROM ? cellTextLightSx : cellTextSx}
                    >
                      {value}
                    </Typography>
                  </Box>
                );
              })}
            </Fragment>
          ))}
          <Box />
          {columns.map((column) => (
            <Typography key={column.id} variant="text-xs-medium" sx={columnLabelSx}>
              {column.label}
            </Typography>
          ))}
        </Box>
        <Typography variant="text-xs-medium" sx={xAxisSx}>
          {xAxisLabel}
        </Typography>
      </Box>
    </Box>
  );
};

const outerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: backrs.spacing['spacing-sm'],
  width: '100%',
});

const yAxisSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  textAlign: 'center',
  flexShrink: 0,
  marginRight: backrs.spacing['spacing-md'],
});

const rightSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  flex: 1,
  minWidth: 0,
});

const gridSx =
  (columnCount: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: `auto repeat(${columnCount}, minmax(0, 1fr))`,
    alignItems: 'center',
    columnGap: backrs.spacing['spacing-sm'],
    rowGap: backrs.spacing['spacing-sm'],
    width: '100%',
  });

const columnLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textAlign: 'center',
});

const rowLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  paddingRight: backrs.spacing['spacing-md'],
  whiteSpace: 'nowrap',
});

const xAxisSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textAlign: 'center',
  width: '100%',
  marginTop: backrs.spacing['spacing-md'],
});

const cellSx =
  (highlighted: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    minHeight: 28,
    // Narrower than this, a ratio plus the minimum height floors the cell at 50px and overflows.
    '@container (min-width: 650px)': {
      aspectRatio: '16 / 9',
    },
    borderRadius: backrs.radius['radius-sm'],
    border: highlighted
      ? `1.5px solid ${backrs.colors['Colors/Base/Black']}`
      : `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  });

const cellTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const cellTextLightSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Base/White'],
});
